/**
 * Workspace-Complete Restore API client (Spec 205)
 *
 * Candidates + definition points/inventory + definition diff + atomic restore launch.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 205-workspace-complete-dr
 */

import apiClient from '../services/api';

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup/workspace-restore';

export type BackupEntityType = 'lakehouse' | 'warehouse';

export interface DefinitionPoint {
  definitionRunId: string;
  pointTime: string;
  supportedCount: number;
  unsupportedCount: number;
  failedCount: number;
  restorableDefinitions: boolean;
}

export interface WorkspaceRestoreCandidate {
  workspaceId: string;
  workspaceName: string;
  lakehouses: number;
  warehouses: number;
  latestDefinitionPoint: DefinitionPoint | null;
  latestDataPoint: { pointTime: string } | null;
}

export interface DefinitionArtifact {
  id: string;
  definitionBackupId: string;
  itemId: string;
  itemType: string;
  itemName: string | null;
  itemVersion: number;
  contentHash: string | null;
  status: 'Captured' | 'Pointer' | 'Unsupported' | 'Failed';
}

export interface WorkspaceDefinitions {
  points: Array<Record<string, unknown>>;
  items: DefinitionArtifact[];
}

export interface DefinitionDiffPart {
  path: string;
  changeKind: 'added' | 'removed' | 'modified' | 'unchanged';
  unifiedDiff: string | null;
}
export interface DefinitionDiff {
  itemId: string;
  itemType: string;
  changed: boolean;
  parts: DefinitionDiffPart[];
}

export interface WorkspaceRestoreDataPoint {
  entityType: BackupEntityType;
  entityId: string;
  runId: string;
}

export interface WorkspaceRestoreLaunchInput {
  workspaceId: string;
  definitionRunId: string;
  targetWorkspaceId: string;
  dataPoints: WorkspaceRestoreDataPoint[];
  createTargetWorkspace?: boolean;
}

export interface WorkspaceRestoreLaunchOutcome {
  status: 'launched' | 'queued' | 'refused';
  restoreRunId?: string;
  executionName?: string; // the container execution to poll for completion
  reason?: string;
  preflight?: { passed: boolean; checks: Array<{ name: string; passed: boolean; detail: string | null }> };
}

export const workspaceRestoreApi = {
  candidates: async (): Promise<WorkspaceRestoreCandidate[]> =>
    (await apiClient.get<Wrapped<WorkspaceRestoreCandidate[]>>(`${BASE}/candidates`)).data,

  definitions: async (workspaceId: string): Promise<WorkspaceDefinitions> =>
    (await apiClient.get<Wrapped<WorkspaceDefinitions>>(`${BASE}/${workspaceId}/definitions`)).data,

  diff: async (
    workspaceId: string,
    fromRunId: string,
    toRunId: string,
    itemId: string
  ): Promise<DefinitionDiff> =>
    (
      await apiClient.get<Wrapped<DefinitionDiff>>(
        `${BASE}/${workspaceId}/diff/${fromRunId}/${toRunId}/${itemId}`
      )
    ).data,

  launch: async (input: WorkspaceRestoreLaunchInput): Promise<WorkspaceRestoreLaunchOutcome> =>
    (await apiClient.post<Wrapped<WorkspaceRestoreLaunchOutcome>>(`${BASE}/launch`, input)).data,

  /** Explore a specific recovery point's contents (the captured items at that point). */
  pointItems: async (workspaceId: string, runId: string): Promise<DefinitionArtifact[]> =>
    (await apiClient.get<Wrapped<DefinitionArtifact[]>>(`${BASE}/${workspaceId}/definitions/${runId}/items`)).data,

  /** Selective / granular restore — bring back the chosen items as NEW items. Lakehouse-parity
   *  targets: the same workspace (always a renamed copy — the name is taken), a different existing
   *  workspace (keeps the name unless taken), or a new temporary workspace.
   *  🛑 There is no `overwrite` (removed 2026-07-17): a restore never replaces a live item. */
  restoreItems: async (input: {
    workspaceId: string;
    definitionRunId: string;
    itemIds: string[];
    targetWorkspaceId?: string;
    createTempWorkspace?: boolean;
  }): Promise<WorkspaceRestoreLaunchOutcome> =>
    (await apiClient.post<Wrapped<WorkspaceRestoreLaunchOutcome>>(`${BASE}/items`, input)).data,
};
